import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../../../api/api';

const FishWaterColumn3D = ({ gridCell, onClose }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const { isAuthenticated } = useAuth0();
  const { getLekCatchabilityData } = useApi();

  const [currentHour, setCurrentHour] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000);

  // Load real catchability data from LEK API
  const [fishCatchabilityData, setFishCatchabilityData] = useState(null);
  const [loadingCatchability, setLoadingCatchability] = useState(false);

  const loadRealCatchabilityData = useCallback(async (speciesId) => {
    if (!speciesId) return;

    setLoadingCatchability(true);
    try {
      // Get 7 days of real catchability data
      const startDate = '2025-01-01';
      const endDate = '2025-01-07';
      
      const response = await getLekCatchabilityData(speciesId, startDate, endDate);
      
      if (response?.data) {
        setFishCatchabilityData(response.data);
      } else {
        setFishCatchabilityData(null);
      }
    } catch (error) {
      console.warn('Failed to load catchability data:', error);
      setFishCatchabilityData(null);
    } finally {
      setLoadingCatchability(false);
    }
  }, [getLekCatchabilityData]);

  // Load catchability on grid change
  useEffect(() => {
    if (gridCell?.species_id) {
      loadRealCatchabilityData(gridCell.species_id);
    }
  }, [gridCell?.species_id, getLekCatchabilityData, loadRealCatchabilityData]);

  // --- Depth sampling helpers ---
  const estimateGulfDepth = useCallback((latitude, longitude) => {
    const distanceFromShore = Math.abs(longitude + 68.5);
    const latitudeFactor = (latitude - 42) * 50;
    const estimatedDepth = 50 + distanceFromShore * 80 + latitudeFactor;
    return Math.max(30, Math.min(600, estimatedDepth));
  }, []);

  const sampleTerrainDepth = useCallback(async (longitude, latitude) => {
    if (!window.Cesium || !window.Cesium.sampleTerrainMostDetailed) {
      return estimateGulfDepth(latitude, longitude);
    }
    try {
      let terrainProvider;
      try {
        terrainProvider = await window.Cesium.CesiumTerrainProvider.fromIonAssetId(2426648, {
          requestVertexNormals: true,
        });
        await terrainProvider.readyPromise;
        if (!terrainProvider.availability) throw new Error('No availability property');
      } catch {
        try {
          const bathy = window.Cesium.Terrain.fromWorldBathymetry();
          await bathy.readyPromise;
          terrainProvider = bathy.provider;
        } catch {
          const worldTerrain = window.Cesium.Terrain.fromWorldTerrain({ requestVertexNormals: true });
          await worldTerrain.readyPromise;
          terrainProvider = worldTerrain.provider;
        }
      }

      if (!terrainProvider?.availability) return estimateGulfDepth(latitude, longitude);

      const samplePoint = window.Cesium.Cartographic.fromDegrees(longitude, latitude);
      const sampled = await window.Cesium.sampleTerrainMostDetailed(terrainProvider, [samplePoint]);

      if (sampled?.[0]?.height !== undefined) {
        const h = sampled[0].height;
        if (h < 0) {
          const depthFeet = Math.abs(h) * 3.28084;
          return Math.max(20, Math.min(2000, depthFeet));
        }
      }
      return estimateGulfDepth(latitude, longitude);
    } catch {
      return estimateGulfDepth(latitude, longitude);
    }
  }, [estimateGulfDepth]);

  const [waterDepth, setWaterDepth] = useState(100);
  useEffect(() => {
    if (gridCell?.longitude && gridCell?.latitude) {
      sampleTerrainDepth(gridCell.longitude, gridCell.latitude)
        .then((d) => setWaterDepth(d))
        .catch(() => setWaterDepth(100));
    }
  }, [gridCell?.longitude, gridCell?.latitude, sampleTerrainDepth]);

  // Data
  const hourlyData = useMemo(() => {
    return fishCatchabilityData?.hourly_results || [];
  }, [fishCatchabilityData]);

  // fish depths vs score
  const getFishDepth = useCallback((s) => {
    if (s >= 0.8) return waterDepth * 0.95;
    if (s >= 0.7) return waterDepth * 0.8;
    if (s >= 0.6) return waterDepth * 0.7;
    if (s >= 0.4) return waterDepth * 0.6;
    return waterDepth * 0.1;
  }, [waterDepth]);

  const generateFishPositions = useCallback((score) => {
    const fishCount = Math.max(5, Math.floor(score * 30));
    const targetDepth = getFishDepth(score);
    const positions = [];
    for (let i = 0; i < fishCount; i++) {
      const depthVariation = score > 0.6 ? 8 : 20;
      const depth = Math.max(5, Math.min(waterDepth - 5, targetDepth + (Math.random() - 0.5) * depthVariation));
      positions.push({
        id: i,
        x: Math.random() * 180 + 60,
        y: depth,
        size: 2 + Math.random() * 3,
        opacity: 0.7 + Math.random() * 0.3,
      });
    }
    return positions;
  }, [waterDepth, getFishDepth]);

  // animation
  useEffect(() => {
    if (!isPlaying || hourlyData.length === 0) return;
    const tick = () => {
      setCurrentHour((p) => (p + 1) % hourlyData.length);
      animationRef.current = setTimeout(tick, playbackSpeed);
    };
    animationRef.current = setTimeout(tick, playbackSpeed);
    return () => animationRef.current && clearTimeout(animationRef.current);
  }, [isPlaying, playbackSpeed, hourlyData.length]);

  // draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || hourlyData.length === 0) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = '#e3f2fd';
    ctx.fillRect(0, 0, width, height);

    // layers
    const layerH = height / 10;
    for (let i = 0; i < 10; i++) {
      const alpha = 0.1 + i * 0.05;
      ctx.fillStyle = `rgba(0, 100, 150, ${alpha})`;
      ctx.fillRect(0, i * layerH, width, layerH);
    }

    // depth markers
    ctx.fillStyle = '#333';
    ctx.font = '10px Arial';
    const depthStep = waterDepth / 10;
    for (let i = 0; i <= 10; i++) {
      const y = (i / 10) * height;
      const d = Math.round(i * depthStep);
      ctx.fillText(`${d}ft`, 5, y + 12);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(30, y);
      ctx.lineTo(width - 5, y);
      ctx.stroke();
    }

    // surface/bottom
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(30, 0);
    ctx.lineTo(width - 5, 0);
    ctx.stroke();

    ctx.strokeStyle = '#8b4513';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(30, height - 1);
    ctx.lineTo(width - 5, height - 1);
    ctx.stroke();

    // fish
    const current = hourlyData[currentHour];
    if (current) {
      const fishPositions = generateFishPositions(current.catchability);

      // Single fixed red color
      const r = 255, g = 0, b = 0;

      fishPositions.forEach((fish) => {
        const y = (fish.y / waterDepth) * height;

        // Fill (solid red with opacity)
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${fish.opacity})`;
        ctx.beginPath();
        ctx.ellipse(fish.x, y, fish.size * 2, fish.size, 0, 0, 2 * Math.PI);
        ctx.fill();

        // Stroke (slightly darker red)
        ctx.strokeStyle = `rgba(${Math.round(r * 0.7)}, ${Math.round(g * 0.7)}, ${Math.round(b * 0.7)}, ${fish.opacity})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(fish.x - fish.size * 2, y);
        ctx.lineTo(fish.x - fish.size * 3, y - fish.size);
        ctx.lineTo(fish.x - fish.size * 3, y + fish.size);
        ctx.closePath();
        ctx.stroke();
      });
    }

    // target zone (bottom 16ft)
    if (waterDepth > 16) {
      const targetStart = ((waterDepth - 16) / waterDepth) * height;
      ctx.fillStyle = 'rgba(0, 255, 0, 0.1)';
      ctx.fillRect(30, targetStart, width - 35, height - targetStart);
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(30, targetStart);
      ctx.lineTo(width - 5, targetStart);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [currentHour, hourlyData, waterDepth, generateFishPositions]);

  if (!gridCell) return null;

  const currentData = hourlyData[currentHour];

  return (
    <div
      className="relative bg-white rounded-lg shadow-2xl w-96 max-h-[70vh] overflow-y-auto pointer-events-auto border border-gray-200"
      style={{ filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.25))' }}
    >
      {/* Header */}
      <div className="p-3">
        {/* Current time and score */}
        {currentData ? (
          <div className="mb-4 p-3 bg-gray-50 rounded border">
            {/* Date / time */}
            <div className="font-medium">
              {new Date(currentData.datetime).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
              })}
            </div>

            {/* Catchability index on its own line */}
            <div className="mt-1 text-sm text-gray-800">
              Catchability index: {currentData.catchability.toFixed(2)}
            </div>

            {/* Depth only (no “real bathymetry”) */}
            <div className="mt-1 text-sm text-gray-600">
              Depth: {waterDepth.toFixed(1)} ft
            </div>
          </div>

        ) : loadingCatchability ? (
          <div className="mb-4 p-3 bg-gray-50 rounded border">
            <div className="text-sm text-orange-600">Loading catchability data...</div>
            <div className="text-xs text-gray-500 mt-1">
              Grid {gridCell.grid_id} • Lat: {gridCell.latitude?.toFixed(4)}, Lon: {gridCell.longitude?.toFixed(4)}
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
            <div className="text-sm text-yellow-700">🐟 Fish movement data not available</div>
            <div className="text-xs text-gray-600 mt-1">
              {!isAuthenticated ? 'Please log in to view fish movement data' : 
               !gridCell?.species_id ? 'No species data available for this location' :
               'No catchability data found for this species and time period'}
            </div>
          </div>
        )}

        {/* Canvas */}
        {hourlyData.length > 0 ? (
          <div className="mb-4">
            <canvas ref={canvasRef} width={280} height={350} className="border-2 border-blue-200 rounded bg-blue-50 w-full" />
          </div>
        ) : (
          <div className="mb-4 p-6 border-2 border-gray-200 rounded bg-gray-50 text-center">
            <div className="text-gray-500">
              {loadingCatchability ? (
                <>
                  <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                  <div>Loading environmental data...</div>
                </>
              ) : (
                <>
                  <div className="text-2xl mb-2">🌊</div>
                  <div>No fish movement data available</div>
                  <div className="text-xs mt-1">
                    {!isAuthenticated ? 'Please log in to view data' : 
                     !gridCell?.species_id ? 'No species data for this location' :
                     'No catchability data found'}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Controls */}
        {hourlyData.length > 0 && (
          <div className="space-y-3 p-3 bg-gray-50 rounded border">
            <div className="flex justify-between items-center">
              <button
                onClick={() => setIsPlaying((p) => !p)}
                className={`px-3 py-2 rounded font-bold text-white ${isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>

              <select
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 text-sm"
              >
                <option value={500}>2x Fast</option>
                <option value={1000}>Normal</option>
                <option value={2000}>Slow</option>
              </select>
            </div>

            <div>
              <input
                type="range"
                min={0}
                max={hourlyData.length - 1}
                value={currentHour}
                onChange={(e) => setCurrentHour(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>Hour {currentHour + 1} of {hourlyData.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FishWaterColumn3D;
